import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Mail, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code & new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    
    if (!email.includes('@')) {
      toast.error('Invalid email format');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email });
      toast.success('Reset code sent to your email!');
      setStep(2);
    } catch (error) {
      // Always show success to prevent email enumeration
      toast.success('If email exists, reset code has been sent');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password-confirm', {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Password reset successful! Please login.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed. Check your code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-8 min-h-screen flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="text-primary fill-primary mx-auto mb-3 pulse-animation" size={40} />
          <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
          <p className="text-gray-400 text-sm">
            {step === 1 ? "Enter your email to receive a reset code" : "Enter the code and your new password"}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleRequestCode} className="space-y-6 flex-1">
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 pl-12 pr-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="you@example.com"
                  required
                  data-testid="reset-email-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50"
              data-testid="reset-submit-button"
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword} className="space-y-6 flex-1">
            {/* Info box */}
            <div className="glass rounded-xl p-4 border border-primary/30">
              <p className="text-gray-300 text-sm">
                Check your email <span className="text-white font-medium">{email}</span> for a 6-digit code
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">Reset Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-14 px-4 text-white text-center text-2xl font-bold placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none tracking-widest"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="reset-code-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">New Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 pl-12 pr-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Min 6 characters"
                  minLength={6}
                  required
                  data-testid="reset-new-password-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 pl-12 pr-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  placeholder="Confirm new password"
                  minLength={6}
                  required
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="reset-confirm-button"
            >
              {loading ? (
                'Resetting...'
              ) : (
                <>
                  <CheckCircle size={20} />
                  Reset Password
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-center text-gray-400 hover:text-white transition-colors text-sm"
            >
              ← Use different email
            </button>
          </form>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 justify-center mx-auto"
          >
            <ArrowLeft size={16} />
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
